"use client";

import { memo, useCallback, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { formatRegionalTime } from "@/lib/i18n/config/languages";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/use-translation";
import {
  clearNavHistory,
  getNavHistory,
  subscribeNavHistory,
} from "../stores/nav-history-store";

// Compiled once at module load — not on every segment render
const UUID_RE = /^[0-9a-f-]{8,}$/;
const NUM_RE = /^\d+$/;

function segmentColor(segment: string): string {
  if (UUID_RE.test(segment)) return "text-muted-foreground/60";
  if (NUM_RE.test(segment)) return "text-amber-500/80";
  return "text-foreground/80";
}

const PathDisplay = memo(function PathDisplay({
  pathname,
}: {
  pathname: string;
}) {
  const parts = pathname.split("/").filter(Boolean);
  return (
    <span className="font-mono text-sm">
      <span className="text-muted-foreground/40">{"/"}</span>
      {parts.map((part, i) => (
        <span key={i}>
          <span className={cn(segmentColor(part))}>{part}</span>
          {i < parts.length - 1 && (
            <span className="text-muted-foreground/40">{"/"}</span>
          )}
        </span>
      ))}
    </span>
  );
});

export function NavHistoryPanel() {
  const t = useTranslations("debug.navigation");
  const { formatLocale, timeZone } = useTranslation("common");
  const entries = useSyncExternalStore(
    subscribeNavHistory,
    getNavHistory,
    getNavHistory,
  );

  const handleClear = useCallback(() => {
    clearNavHistory();
  }, []);

  if (entries.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        {t("empty")}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">
          {t("routeCount", { count: entries.length })}
        </span>
        <button
          onClick={handleClear}
          className="text-sm px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("clear")}
        </button>
      </div>
      <div className="space-y-0">
        {entries.map((entry, idx) => (
          <div
            key={entry.id}
            className={cn(
              "flex items-center gap-3 py-1.5 px-2 rounded text-sm group",
              idx === 0
                ? "bg-emerald-500/5 border border-emerald-500/20"
                : "hover:bg-muted/20",
            )}
          >
            <span className="text-muted-foreground/50 w-16 shrink-0 text-sm">
              {formatRegionalTime(entry.timestamp, formatLocale, timeZone)}
            </span>
            <PathDisplay pathname={entry.pathname} />
            {idx === 0 && (
              <span className="ml-auto text-sm text-emerald-500 shrink-0">
                {t("current")}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
